<template>
  <div>
    <div class="card-body">
      <!-- Initiate the statement process -->
      <div class="text-right mb-4" v-if="!readonly">
        <div class="btn-group">
          <button
            type="button"
            id="request-statement"
            class="btn btn-primary dropdown-toggle"
            data-toggle="dropdown"
          >
            Request Statement
          </button>
          <div class="dropdown-menu">
            <a
              v-for="type in statementTypes"
              :key="type.name"
              class="dropdown-item"
              href="#"
              @click.prevent="selectWorkflow(type)"
            >
              {{ type.label }}
            </a>
          </div>
        </div>
      </div>

      <div class="clearfix"></div>
      <hr />

      <!-- Display the list of statements -->
      <div v-if="statements.length > 0">
        <h5>Statements</h5>

        <table class="table table-bordered table-striped">
          <thead>
            <tr>
              <th>Statement Name</th>
              <th>Recipient</th>
              <th>Send At</th>
              <th>ReSend At</th>
              <th>Status</th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            <tr v-bind:key="index" v-for="(statement, index) in statements">
              <td>{{ statement.statementType?.label }}</td>
              <td>{{ statement.statementable?.full_name }}</td>
              <td>{{ statement.created_at }}</td>
              <td>{{ statement.resend_at }}</td>
              <td>{{ statement.status }}</td>
              <td>
                <form-button
                  v-if="statement.status === 'Completed'"
                  level="primary"
                  :indicator="downloading"
                  @clicked="downloadStatement(statement)"
                >
                  Download PDF
                </form-button>
                <form-button
                  v-if="statement.status !== 'Completed' && !readonly"
                  type="button"
                  class="btn btn-warning"
                  @clicked="resendStatementProcess(statement)"
                  :indicator="resendProcessing === statement.identifier"
                >
                  Resend
                </form-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p>No statements found.</p>
      </div>

      <!-- Recipient Selection Modal -->
      <modal :show="showRecipientModal" v-on:close="closeModal">
        <div slot="title" class="text-center">
          <h5>
            Select the {{ capitalisedSubject }} to send the
            {{ selectedWorkflow?.label }} workflow to:
          </h5>
        </div>
        <div class="row">
          <div class="col-xl-12">
            <selectlist
              name="recipient"
              label="Recipient"
              label-name="full_name"
              v-model="selectedRecipientId"
              placeholder="Please Select"
              :options="availableRecipients"
            ></selectlist>
          </div>
        </div>
        <div slot="footer">
          <button type="button" v-on:click="closeModal" class="btn btn-danger">
            Close
          </button>
          <form-button
            :indicator="processing"
            class="btn btn-primary"
            @clicked="startStatementProcess"
            :disabled="!selectedRecipientId"
          >
            Send
          </form-button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      processing: false,
      resendProcessing: null,
      selectedWorkflow: null,
      selectedRecipientId: null,
      showRecipientModal: false,
      availableRecipients: [],
      statements: [],
      downloading: false
    };
  },

  computed: {
    client() {
      return this.$store.getters['Incident/client'];
    },
    thirdParties() {
      return this.$store.getters['Incident/thirdParties'];
    },
    witnesses() {
      return this.$store.getters['Incident/witnesses'];
    },
    statementTypes() {
      return this.$store.getters['StatementTypes/all'];
    },
    capitalisedSubject() {
      if (!this.selectedWorkflow?.subject) return '';
      return this.selectedWorkflow.subject
        .replace(/_/g, ' ')
        .replace(
          /\w\S*/g,
          txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
    }
  },

  mounted() {
    this.$store.commit('StatementTypes/retrieve');
    this.fetchStatements();
  },

  methods: {
    selectWorkflow(workflow) {
      if (this.readonly) return;
      this.selectedWorkflow = workflow;

      if (workflow.subject === 'policyholder') {
        this.selectedRecipientId = this.client?.id;

        // Check if contact methods are missing for policyholder
        if (this.hasNoContactMethods(workflow.subject, this.client)) {
          window.toastify.error(
            `No contact methods available for the ${this.capitalisedSubject}.`
          );
          return;
        }

        this.startStatementProcess();
      } else {
        const recipients =
          workflow.subject === 'third_party'
            ? this.thirdParties
            : this.witnesses;
        this.availableRecipients = recipients;

        if (recipients.length === 1) {
          this.selectedRecipientId = recipients[0].id;

          // Check if contact methods are missing for the single recipient
          if (this.hasNoContactMethods(workflow.subject, recipients[0])) {
            window.toastify.error(
              `No contact methods available for the ${this.capitalisedSubject}.`
            );
            return;
          }

          this.startStatementProcess();
        } else if (recipients.length > 1) {
          this.showRecipientModal = true;
        } else {
          window.toastify.error(
            `No ${this.capitalisedSubject}s available for this workflow.`
          );
        }
      }
    },

    startStatementProcess() {
      if (this.readonly) return;
      // Find the selected recipient from availableRecipients
      const recipient =
        this.availableRecipients.find(r => r.id === this.selectedRecipientId) ||
        this.client;

      // Check if contact methods are missing for the selected recipient
      if (this.hasNoContactMethods(this.selectedWorkflow.subject, recipient)) {
        window.toastify.error(
          `No contact methods available for the selected ${this.capitalisedSubject}.`
        );
        return;
      }

      this.processing = true;
      window.Ajax.post(`/incidents/${this.incident}/statements/send`, {
        type: this.selectedWorkflow.name,
        subject: this.selectedWorkflow.subject,
        party_id: this.selectedRecipientId
      })
        .then(() => {
          window.toastify.success('Statement process started successfully!');
          this.fetchStatements();
          this.processing = false;
          this.showRecipientModal = false;
          this.selectedRecipientId = null;
          this.selectedWorkflow = null;
        })
        .catch(() => {
          window.toastify.error('Failed to start the statement process.');
          this.processing = false;
        });
    },

    resendStatementProcess(statement) {
      if (this.readonly) return;
      this.resendProcessing = statement.identifier;
      window.Ajax.get(
        '/incidents/' + this.incident + '/statements/' + statement.identifier
      )
        .then(() => {
          window.toastify.success('Statement resend successfully!');
          this.fetchStatements();
          this.resendProcessing = null;
        })
        .catch(() => {
          window.toastify.error('Failed to resend the statement process.');
          this.resendProcessing = null;
        });
    },

    downloadStatement(statement) {
      this.downloading = true;

      window.Ajax.download(
        '/incidents/' +
          this.incident +
          '/statements/' +
          statement.identifier +
          '/download'
      )
        .then(() => (this.downloading = false))
        .catch(() => {
          this.downloading = false;

          window.toastify.error(
            'Oops',
            'An error occurred while downloading the PDF'
          );
        });
    },

    fetchStatements() {
      window.Ajax.get('/incidents/' + this.incident + '/statements')
        .then(response => {
          this.statements = response.data;
        })
        .catch(() => {
          window.toastify.error('Failed to fetch the statements.');
        });
    },

    closeModal() {
      this.showRecipientModal = false;
      this.selectedRecipientId = null;
      this.selectedWorkflow = null;
    },

    hasNoContactMethods(type, party) {
      const {
        email_address,
        telephone,
        home_telephone,
        work_telephone,
        mobile_telephone
      } = party || {};
      return !(
        email_address ||
        telephone ||
        home_telephone ||
        work_telephone ||
        mobile_telephone
      );
    }
  },
  watch: {
    processing(value) {
      let Button = window.Ladda.create(
        this.$el.querySelector('#request-statement')
      );

      if (value) {
        Button.start();
      } else {
        Button.stop();
      }
    }
  }
};
</script>
